import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { getRecommendations, listChampions, listRoles } from './api/championsApi';
import ChampionGrid from './components/ChampionGrid';
import ChampionIcon from './components/ChampionIcon';
import DraftBoard from './components/DraftBoard';
import Recommendations from './components/Recommendations';
import YourChampion from './components/YourChampion';
import { useDebouncedValue } from './hooks/useDebouncedValue';
import { BAN_COUNT, TEAM_SIZE, type Mode } from './draft';
import { POSITION_TAGS } from './positions';
import { Droppable } from './dnd';
import type { Bucket, DragData, DropData } from './dnd';
import type { ChampionSummary, Pick, PositionChoice, Recommendation, RecFor } from './types';

type Draft = Record<Bucket, string[]>;
const CAP: Record<Bucket, number> = { ally: TEAM_SIZE, enemy: TEAM_SIZE, ban: BAN_COUNT };
const EMPTY: Draft = { ally: [], enemy: [], ban: [] };

/** Rewrites recommendation reason text from the enemy's point of view, since we
 * get enemy recs by swapping team labels in the request (see the effect below). */
function swapPerspective(reason: string): string {
  const TOK = '⁣ENEMY⁣';
  return reason
    .replace(/your team/gi, TOK)
    .replace(/the enemy/gi, 'your team')
    .split(TOK)
    .join('the enemy team');
}

export default function App() {
  const [champions, setChampions] = useState<ChampionSummary[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [patch, setPatch] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [meIndex, setMeIndex] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('sr');
  // The active target row: where a clicked champion lands. Starts on bans (SR),
  // but any row tab can switch it — nothing is locked to a fixed draft order.
  const [target, setTarget] = useState<Bucket>('ban');

  // Recommendation controls.
  const [recFor, setRecFor] = useState<RecFor>('ally');
  const [position, setPosition] = useState<PositionChoice>('any');
  const [focusRoles, setFocusRoles] = useState<string[]>([]);
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    Promise.all([listChampions(), listRoles()])
      .then(([list, r]) => {
        setChampions(list.champions);
        setPatch(list.version);
        setRoles(r);
      })
      .catch((e) => setLoadError(e.message ?? 'Failed to load champions'));
  }, []);

  const byId = useMemo(() => new Map(champions.map((c) => [c.id, c])), [champions]);
  const usedIds = useMemo(
    () => new Set([...draft.ally, ...draft.enemy, ...draft.ban]),
    [draft],
  );
  const myChampion = byId.get(draft.ally[meIndex] ?? '') ?? null;
  const totalPlaced = draft.ally.length + draft.enemy.length + draft.ban.length;

  const isUsed = (d: Draft, id: string) =>
    d.ally.includes(id) || d.enemy.includes(id) || d.ban.includes(id);

  // Place a champion into a row (append if there's room), then auto-advance the
  // active target: after a team pick, flip to the other team; bans keep filling.
  const placeInto = useCallback((bucket: Bucket, id: string) => {
    setDraft((prev) => {
      if (isUsed(prev, id) || prev[bucket].length >= CAP[bucket]) return prev;
      return { ...prev, [bucket]: [...prev[bucket], id] };
    });
    if (bucket === 'ally') setTarget('enemy');
    else if (bucket === 'enemy') setTarget('ally');
  }, []);

  // Grid click → active target row. Recommendation click → the advised team.
  const guidedAdd = useCallback((c: ChampionSummary) => placeInto(target, c.id), [placeInto, target]);
  const recAdd = useCallback((c: ChampionSummary) => placeInto(recFor, c.id), [placeInto, recFor]);

  // Drag-drop / move: remove from wherever it is, then insert into `bucket` at
  // `index` (or append). Handles grid→row, row→row, and reorder within a row.
  const placeChampion = useCallback((id: string, bucket: Bucket, index: number | null) => {
    setDraft((prev) => {
      const next: Draft = {
        ally: prev.ally.filter((x) => x !== id),
        enemy: prev.enemy.filter((x) => x !== id),
        ban: prev.ban.filter((x) => x !== id),
      };
      const arr = next[bucket];
      if (arr.length >= CAP[bucket]) return prev; // target full
      const at = index == null ? arr.length : Math.max(0, Math.min(index, arr.length));
      arr.splice(at, 0, id);
      return next;
    });
  }, []);

  const removeChampion = useCallback((id: string) => {
    setDraft((prev) => ({
      ally: prev.ally.filter((x) => x !== id),
      enemy: prev.enemy.filter((x) => x !== id),
      ban: prev.ban.filter((x) => x !== id),
    }));
  }, []);

  const clear = useCallback(() => {
    setDraft(EMPTY);
    setTarget(mode === 'sr' ? 'ban' : 'ally');
  }, [mode]);

  // Switching to ARAM drops any bans (ARAM has none) and moves the target off bans.
  const changeMode = useCallback((m: Mode) => {
    setMode(m);
    if (m === 'aram') {
      setDraft((prev) => ({ ...prev, ban: [] }));
      setTarget((t) => (t === 'ban' ? 'ally' : t));
    }
  }, []);

  const toggleFocusRole = useCallback((role: string) => {
    setFocusRoles((cur) => (cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role]));
  }, []);

  const onDragStart = (e: DragStartEvent) => {
    setActiveId((e.active.data.current as DragData | undefined)?.championId ?? null);
  };
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const drag = e.active.data.current as DragData | undefined;
    if (!drag) return;
    const drop = e.over?.data.current as DropData | undefined;
    if (drop && 'bucket' in drop) {
      // Dropped on a slot → place / move there.
      placeChampion(drag.championId, drop.bucket, drop.index);
    } else if (drag.from !== 'grid') {
      // Dropped on the remove zone, or off the board entirely → take a board
      // champion off. (Dragging a grid champion into empty space is a no-op.)
      removeChampion(drag.championId);
    }
  };

  // Recompute recommendations whenever the draft or the rec controls change.
  const draftKey = useDebouncedValue(JSON.stringify(draft), 250);
  useEffect(() => {
    if (champions.length === 0) return;
    // Show recs once there's something to reason from: any pick/ban, OR — when
    // advising your team — a role/focus selected (so a first-pick with a chosen
    // role gets suggestions off the bat, before anything is on the board).
    const usePosition = recFor === 'ally' && position !== 'any';
    const hasBasis = totalPlaced > 0 || (recFor === 'ally' && (usePosition || focusRoles.length > 0));
    if (!hasBasis) {
      setRecs([]);
      return;
    }
    // For enemy recs we simply flip the team labels and ask the same endpoint to
    // advise "ally" — the exclusion of picked/banned champions is symmetric.
    const allyTeam = recFor === 'ally' ? 'ally' : 'enemy';
    const enemyTeam = recFor === 'ally' ? 'enemy' : 'ally';
    const picks: Pick[] = [
      ...draft.ally.map((id) => ({ id, team: allyTeam as Pick['team'] })),
      ...draft.enemy.map((id) => ({ id, team: enemyTeam as Pick['team'] })),
    ];
    // Filtering happens server-side (before ranking) so a lane draws from every
    // champion who plays it, not just the global top-N. rolesAny = your lane (≥1
    // tag); rolesAll = dual-type focus (all tags). Show more when filtering.
    const rolesAny = usePosition ? POSITION_TAGS[position] : [];
    const filtering = usePosition || focusRoles.length > 0;
    const limit = filtering ? 40 : 6;

    let cancelled = false;
    setRecLoading(true);
    setRecError(null);
    getRecommendations({ picks, bans: draft.ban, rolesAny, rolesAll: focusRoles, limit })
      .then((res) => {
        if (cancelled) return;
        let list = res.recommendations;
        if (recFor === 'enemy') {
          list = list.map((r) => ({ ...r, reasons: r.reasons.map(swapPerspective) }));
        }
        setRecs(list.slice(0, filtering ? 12 : 6));
      })
      .catch((e) => !cancelled && setRecError(e.message ?? 'Failed to get recommendations'))
      .finally(() => !cancelled && setRecLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, recFor, position, focusRoles, champions.length]);

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark">LoL</span>
          <span className="brand-name">Coach</span>
        </div>
        <p className="tagline">
          Draft-time champion recommendations — drag in who’s already picked, get the best next pick.
        </p>
        {patch && <span className="patch">Patch {patch}</span>}
      </header>

      {loadError ? (
        <p className="load-error">{loadError}</p>
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <main className="layout">
            <div className="layout-left">
              <div className="draft-area">
                <DraftBoard
                  champions={byId}
                  allies={draft.ally}
                  enemies={draft.enemy}
                  bans={draft.ban}
                  target={target}
                  onTargetChange={setTarget}
                  onRemove={removeChampion}
                  onClear={clear}
                  meIndex={meIndex}
                  onMeIndexChange={setMeIndex}
                  mode={mode}
                  onModeChange={changeMode}
                />
                <YourChampion champion={myChampion} playerNumber={meIndex + 1} />
              </div>
              <Droppable id="remove-zone" data={{ remove: true }} className="remove-zone">
                <ChampionGrid
                  champions={champions}
                  roles={roles}
                  usedIds={usedIds}
                  onPick={guidedAdd}
                />
              </Droppable>
            </div>
            <aside className="layout-right">
              <Recommendations
                recFor={recFor}
                onRecForChange={setRecFor}
                position={position}
                onPositionChange={setPosition}
                allRoles={roles}
                focusRoles={focusRoles}
                onToggleFocusRole={toggleFocusRole}
                onClearFocus={() => setFocusRoles([])}
                recommendations={recs}
                loading={recLoading}
                error={recError}
                onPick={recAdd}
              />
            </aside>
          </main>

          <DragOverlay>
            {activeId && byId.get(activeId) ? (
              <ChampionIcon champion={byId.get(activeId)!} size={56} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
